import type { MouseEvent } from "react";
import { useNavigate } from "react-router";
import { create } from "zustand";
import { Building2, LogOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useThemeContext } from "./ThemeContext";
import { useAuth } from "@/context/AuthContext";

/** Venue onboarding lives on venuelista.com. */
export const ADD_VENUE_URL = "https://venuelista.com/add-venue";

const useAddVenueDialogStore = create<{ open: boolean; setOpen: (open: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/**
 * Gate for every "Add Venue" / "List Your Venue" entry point (all go to /add-venue):
 * - `hidden`: venue accounts already have a venue — callers hide the link/button.
 * - `guardLink`: onClick for a <Link to="/add-venue">; for members it cancels navigation and
 *   opens the logout prompt instead.
 * - `goToAddVenue`: same check for buttons that navigate programmatically.
 */
export function useAddVenueGate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const setOpen = useAddVenueDialogStore((s) => s.setOpen);
  const isMember = user?.accountType === "member";

  return {
    hidden: user?.accountType === "venue",
    guardLink: (e: MouseEvent) => {
      if (!isMember) return;
      e.preventDefault();
      setOpen(true);
    },
    goToAddVenue: () => {
      if (isMember) setOpen(true);
      else window.location.assign(ADD_VENUE_URL);
    },
  };
}

/**
 * "You're signed in as a Member" prompt — venues are separate accounts, so a member must log
 * out before adding one. Mounted once in RootLayout; opened via useAddVenueGate. Styling
 * mirrors MemberOnlyDialog.
 */
export function AddVenueMemberDialog() {
  const { isLight, isGlassy } = useThemeContext();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const open = useAddVenueDialogStore((s) => s.open);
  const setOpen = useAddVenueDialogStore((s) => s.setOpen);
  const close = () => setOpen(false);

  const handleLogoutAndAddVenue = () => {
    close();
    logout();
    window.location.assign(ADD_VENUE_URL);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent showCloseButton={false} className="max-w-sm text-center border-0 bg-transparent shadow-none p-0">
        <div className="relative p-1">
          <div
            className={`absolute inset-0 rounded-3xl blur-xl ${
              isGlassy
                ? "bg-gradient-to-r from-[#ff3366]/40 to-orange-500/40"
                : "bg-gradient-to-r from-[#ff3366]/30 to-orange-500/30"
            }`}
          />
          <div
            className={`relative rounded-2xl border p-6 sm:p-7 ${
              isLight ? "bg-white border-gray-200" : "bg-[#0f0f0f] border-[#2a2a2a]"
            }`}
          >
            <DialogHeader className="items-center">
              <div className="relative mb-3">
                <div className="absolute inset-0 rounded-full bg-[#ff3366]/30 blur-md animate-pulse" />
                <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#ff3366] to-orange-500 flex items-center justify-center shadow-lg shadow-[#ff3366]/25">
                  <Building2 className="text-white" size={24} />
                </div>
              </div>
              <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
                You're signed in as a Member
              </DialogTitle>
              <DialogDescription asChild>
                <p className={`text-sm leading-relaxed ${isLight ? "text-gray-600" : "text-gray-300"}`}>
                  Venues use a separate account. Log out of your member account, then sign up with your
                  business email to create and add your venue.
                </p>
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="flex-col gap-2 sm:flex-col mt-5">
              <button
                type="button"
                onClick={handleLogoutAndAddVenue}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#ff3366] to-orange-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <LogOut size={16} />
                Log Out &amp; Add Venue
              </button>
              <button
                type="button"
                onClick={close}
                className={`w-full px-6 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isLight ? "text-gray-500 hover:bg-gray-100" : "text-gray-400 hover:bg-white/[0.06]"
                }`}
              >
                Cancel
              </button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
